import { isExpectedSessionEndError } from "./resolveAuthFailureReport";

/**
 * Retry policy for a TRANSIENT silent token renewal failure, before letting it
 * fall through to a full-page sign-in redirect.
 *
 * Why this exists: `signinSilent` (oidc-client-ts, driven by
 * `automaticSilentRenew` + the on-refocus re-attempt) currently rejects on the
 * first failure of any kind. A single transient blip — Keycloak
 * `temporarily_unavailable`/`server_error` under load, a dropped fetch, or a
 * silent-renew iframe timeout — therefore bounces an active user to login and
 * lands them on a stale screen (e.g. an expired Exchange/Pay quote, which then
 * trips CUSTOMER-BFF-J). A short bounded retry clears those at the source and
 * de-noises CUSTOMER-UI-56, mirroring the boot-fetch retry in
 * `redux/services/shouldRetryRequest.ts`.
 *
 * Deliberately narrow, like the GET-retry policy:
 *  - A genuine session-end (`invalid_grant` / "Token is not active" /
 *    `login_required` / …) is NEVER retried — the refresh token is dead, so a
 *    retry is futile and could even worsen a rotation-reuse race. We fall
 *    straight through to the existing redirect path. (Reuses
 *    `isExpectedSessionEndError` so this stays in lockstep with the alert gate.)
 *  - Only KNOWN transient signals retry (allowlist). Anything unrecognised is
 *    treated as deterministic and not retried.
 */

/** Maximum retries in addition to the initial attempt. Total backoff
 * (0.4 + 0.8 + 1.6 + 3.2 ≈ 6s for the 4 retries) stays well inside the 60s
 * pre-expiry renewal window so retries never outlive the access token —
 * and a single bounded mid-window re-attempt (AuthProvider) plus the
 * last-chance attempt at expiry cover outages longer than the backoff. */
export const MAX_SILENT_RENEW_RETRIES = 4;

// Signals that a renewal failure is transient and a backoff is the remedy:
// OAuth "try again later" codes, browser network-failure phrasings (Chromium
// / Firefox / Safari), and gateway statusText phrasings (a Keycloak behind a
// restarting ingress answers 502/503/504 with an HTML body, which surfaces
// as an Error carrying the status text rather than an OAuth ErrorResponse).
// Deliberately excludes timeouts: oidc-client-ts's SilentRenewService already
// self-heals an `ErrorTimeout` with its OWN retry (it doesn't raise
// silentRenewError for one), so retrying the timeout shape here too would
// stack a second retry layer and multiply renewal traffic. We only cover the
// network/server shapes oidc does NOT auto-retry.
const TRANSIENT_RENEW_MARKERS = [
  "temporarily_unavailable",
  "server_error",
  "failed to fetch",
  "networkerror",
  "network error",
  "load failed",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
];

/**
 * Best-effort string extraction across the shapes `signinSilent` can reject
 * with: an oidc-client-ts `ErrorResponse` ({ error, message }), an
 * `ErrorTimeout` ({ name }), or a plain `Error` ({ message }).
 */
function extractErrorText(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const e = error as { message?: unknown; error?: unknown; name?: unknown };
    return [e.message, e.error, e.name]
      .filter((part): part is string => typeof part === "string")
      .join(" ");
  }
  return "";
}

/**
 * Whether a silent-renew failure is a definitive session-end
 * (invalid_grant / "Token is not active" / …) across the error shapes
 * `signinSilent` can reject with — the object-shape counterpart of
 * `isExpectedSessionEndError`, which takes a bare message string.
 */
export function isSessionEndRenewError(error: unknown): boolean {
  return isExpectedSessionEndError(extractErrorText(error));
}

/**
 * Whether a silent-renew failure is a TRANSIENT condition a retry could clear,
 * as opposed to a deterministic session-end / config error that fails
 * identically every time.
 */
export function isTransientRenewError(error: unknown): boolean {
  const text = extractErrorText(error).toLowerCase();
  if (!text) return false;
  // A definitive session-end fails identically on retry — the user must
  // re-authenticate. Check first so it can never be mistaken for transient.
  if (isExpectedSessionEndError(text)) return false;
  return TRANSIENT_RENEW_MARKERS.some((marker) => text.includes(marker));
}

/**
 * @param error   the rejection from the failed `signinSilent` attempt
 * @param attempt 1-based retry counter (1 when deciding on the first retry)
 */
export function shouldRetrySilentRenew(
  error: unknown,
  attempt: number
): boolean {
  if (attempt > MAX_SILENT_RENEW_RETRIES) return false;
  return isTransientRenewError(error);
}
